import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { ShareItem, ShareObjectStatus } from 'types/Share';
import { NotificationType } from 'components/message/AlertContext';
import queryRequestItems from 'api/ShareObject/queryRequestItems';
import { ShareableObjectFilter } from 'types/Filters';
import { UserRoleForDataset } from 'types/Dataset';
import queryRole from 'api/Dataset/queryRole';
const PagedResponseDefault = {
    count: 1,
    page: 1,
    pages: 1,
    hasNext: false,
    hasPrevious: false,
    nodes: [],
};
interface ShareableItemsList {
    count: number;
    page: number;
    pages: number;
    hasNext: boolean;
    hasPrevious: boolean;
    nodes: ShareItem[];
}

interface NotificationProps {
    message: string;
    type: NotificationType;
}

export default function useShareObject(
    shareUri: string,
    filter: ShareableObjectFilter,
    resourceUri?: string,
) {
    const client = useClient();
    const [notification, setNotification] = useState<NotificationProps | null>(
        null,
    );
    const [loading, setLoading] = useState<boolean>(true);
    const [shareableItems, setShareableItems] =
        useState<ShareableItemsList>(PagedResponseDefault);
    const [role, setRole] = useState<UserRoleForDataset>();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (resourceUri) {
                    const userRoleResponse = await client.query(
                        queryRole(resourceUri),
                    );

                    setRole(
                        userRoleResponse.data.getDataset.userRoleForDataset,
                    );
                }

                if (notification === null) {
                    const response = await client.query(
                        queryRequestItems(shareUri, filter),
                    );
                    if (!response.errors) {
                        const data = response.data.getShareObject;
                        if (data.items.count === 0) {
                            setNotification({
                                type: 'info',
                                message:
                                    'Looks like you have already requested access to everything within this dataset for the selected environment and target group.',
                            });
                        } else {
                            data.items.nodes.sort(
                                (a: ShareItem, b: ShareItem) => {
                                    const nameA = a.itemName.toUpperCase(); // ignore upper and lowercase
                                    const nameB = b.itemName.toUpperCase(); // ignore upper and lowercase
                                    if (nameA < nameB) {
                                        return -1;
                                    }
                                    if (nameA > nameB) {
                                        return 1;
                                    }
                                    // names must be equal
                                    return 0;
                                },
                            );

                            setShareableItems({
                                ...data.items,
                                nodes: data.items.nodes.map(
                                    (node: ShareItem) => ({
                                        ...node,
                                        name: node.itemName + node.itemType,
                                        checked: node.status !== null,
                                    }),
                                ),
                            });
                        }
                    } else {
                        setNotification({
                            type: 'alert',
                            message: response.errors[0]?.message,
                        });
                    }
                }
            } catch (notification) {
                console.error(notification);
            } finally {
                setLoading(false);
            }
        };
        if (client && shareUri) {
            fetchData();
        }
    }, [client, filter, resourceUri, shareUri]);

    return { notification, loading, shareableItems, role };
}
